import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Button, Card, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';

import { SpecialEventsQuery, TournamentQuery } from '../../../../../../__gqltypes__';
import { GET_SPECIAL_EVENTS } from '../../SpecialEvent/graphql';
import { ADD_SPECIAL_EVENT_TOURNAMENT, REMOVE_SPECIAL_EVENT_TOURNAMENT } from '../graphql';

type AdminSpecialEvent = NonNullable<NonNullable<TournamentQuery["tournament"]>["adminSpecialEvent"]>
type Props = {
  tournamentId: string;
  specialEvent: AdminSpecialEvent | null;
  refetch: () => void;
};

const SpecialEventSelector = ({ tournamentId, specialEvent, refetch }: Props) => {
  const { data } = useQuery<SpecialEventsQuery>(GET_SPECIAL_EVENTS, {
    fetchPolicy: 'no-cache',
  });
  const [addSpecialEventTournament, { loading: addSpecialEventLoading }] = useMutation(ADD_SPECIAL_EVENT_TOURNAMENT, {
    onCompleted: ({ addSpecialEventTournament: _data }) => {
      setCurrentSpecialData(_data.specialEvent);
    },
  });
  const [removeSpecialEventTournament, { loading: removeSpecialEventLoading }] = useMutation(
    REMOVE_SPECIAL_EVENT_TOURNAMENT,
    {
      onCompleted: () => {
        setCurrentSpecialData(null);
        selectSpecialEvent(null);
      },
    }
  );

  // useState
  const [currentSpecialEvent, setCurrentSpecialData] = useState<AdminSpecialEvent | null>(null);
  const [selectedSpecialEvent, selectSpecialEvent] = useState<string | null>(null);

  // useEffect
  useEffect(() => {
    if (!_.isNil(specialEvent)) {
      setCurrentSpecialData(specialEvent);
      selectSpecialEvent(specialEvent.id);
    }
  }, [specialEvent]);

  useEffect(() => {
    if (!_.isNil(data) && _.isNil(selectedSpecialEvent) && data.liveOps.specialEvents.list.length > 0) {
      selectSpecialEvent(data.liveOps.specialEvents.list[0].id);
    }
  }, [data, selectedSpecialEvent]);

  const onLink = async (shouldUpdateTournamentRankGroups?: boolean) => {
    if (!_.isNil(selectedSpecialEvent)) {
      await addSpecialEventTournament({
        variables: { input: { specialEventId: selectedSpecialEvent, tournamentId, shouldUpdateTournamentRankGroups } },
      });
      if (shouldUpdateTournamentRankGroups) {
        // refetch to update rankgroups section
        refetch();
      }
    }
  };

  const onUnlink = () => {
    if (!_.isNil(selectedSpecialEvent)) {
      removeSpecialEventTournament({
        variables: { input: { specialEventId: selectedSpecialEvent, tournamentId } },
      });
    }
  };

  return (
    <Card className="mt-3 mb-3">
      <Card.Body className="text-center">
        <Card.Title>Special Events</Card.Title>
        {!currentSpecialEvent ? (
          data && data.liveOps.specialEvents && data.liveOps.specialEvents.list.length > 0 ? (
            <>
              <Form.Control
                value={selectedSpecialEvent ?? undefined}
                onChange={(e) => {
                  selectSpecialEvent(e.target.value ?? null);
                }}
                as="select"
                className="mt-3 mb-2"
              >
                {data.liveOps.specialEvents.list.map(({ name, id }) => (
                  <option key={id} value={id}>
                    {name} ({id})
                  </option>
                ))}
              </Form.Control>
              <Button style={{ marginRight: '1rem' }} disabled={addSpecialEventLoading} onClick={() => onLink(false)}>
                Link and keep current config
              </Button>

              <Button disabled={addSpecialEventLoading} onClick={() => onLink(true)}>
                Link and update current config
              </Button>
            </>
          ) : (
            <div>no special event available</div>
          )
        ) : (
          <>
            <div className="mb-3">
              current special event:{' '}
              <Link to={`/sp3/liveops/specialevent/${currentSpecialEvent.id}`}>
                {currentSpecialEvent.name} ({currentSpecialEvent.id})
              </Link>
            </div>
            <Button variant="danger" disabled={removeSpecialEventLoading} onClick={onUnlink}>
              Unlink
            </Button>
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default SpecialEventSelector;
